import React, { useState } from "react";
import { Button, Box } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { AppDispatch } from "../../../store";
import AuthPageLayout from "../../../components/layout/AuthPageLayout";
import { forgotPassword } from "../../../store/actions/authActions";
import { ROUTES, COOKIE_KEYS } from "../../../constants";
import { getCookie } from "../../../utils/cookieUtils";
import { images } from "../../../assets";
import {
  CheckEmailContainer,
  CheckEmailTitle,
  CheckEmailSubtitle,
  CheckEmailQuestion,
  CheckEmailActions,
} from "./CheckEmailPage.styled";
import { Form } from "../../../components/Form";
import FormFieldErrors from "../../../components/Form/FormFieldErrors";
import { useApiErrorHandling } from "../../../hooks";

const CheckEmailPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const [isResending, setIsResending] = useState(false);

  const { apiErrors, handleApiError } = useApiErrorHandling({
    defaultErrorMessage: "Incorrect email or password. Please try again.",
  });

  const handleGoToLogin = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleResend = async () => {
    try {
      setIsResending(true);

      // Get email from sessionStorage
      const email = getCookie(COOKIE_KEYS.FORGOT_EMAIL_HINT);

      if (!email) {
        handleApiError(
          "Email not found. Please try the forgot password process again."
        );
        return;
      }

      // Send the same request as ForgotPasswordPage
      await dispatch(forgotPassword(email)).unwrap();

      // Show success message or navigate back to check-email
    } catch (errors: unknown) {
      handleApiError(errors);
    } finally {
      setIsResending(false);
    }
  };

  return (
    <AuthPageLayout withHeaderImage={true}>
      <CheckEmailContainer>
        <Box
          component="img"
          src={images.checkEmail}
          alt="Check email icon"
          sx={{ width: "135px", marginBottom: "25px" }}
        />

        <CheckEmailTitle>{t("checkEmail.title")}</CheckEmailTitle>

        <CheckEmailSubtitle>{t("checkEmail.subtitle")}</CheckEmailSubtitle>

        <CheckEmailQuestion>{t("checkEmail.question")}</CheckEmailQuestion>

        <Box sx={{ marginTop: "-35px", marginBottom: "25px" }}>
          <Form
            onSubmit={() => {}}
            validationRules={{}}
            showFieldErrors={false}
          >
            <FormFieldErrors apiErrors={apiErrors} />
          </Form>
        </Box>

        <CheckEmailActions>
          <Button variant="outlined" onClick={handleGoToLogin}>
            {t("checkEmail.goToLogin")}
          </Button>

          <Button
            variant="contained"
            onClick={handleResend}
            disabled={isResending || !!apiErrors.length}
          >
            {isResending ? t("checkEmail.resending") : t("checkEmail.resend")}
          </Button>
        </CheckEmailActions>
      </CheckEmailContainer>
    </AuthPageLayout>
  );
};

export default CheckEmailPage;
